import React, { useEffect, useState } from 'react';
import { useTranslations } from 'use-intl';
import { Typography } from '@/UIlibrary/Typography/Typography';
import {
	MessageCircle,
	Plus,
	Cpu,
	AlertTriangle,
	AlertCircle,
	Archive,
	UserCheck,
	Send
} from 'react-feather';
import Button from '@/UIlibrary/Button/Button';
import { Chat, ChatTag } from './types';
import { subscribeToChats, useChatStore } from './useChat';
import FiltersAndSearch from './FiltersAndSearch';
import TemplateMessageModal from './ChatMenuOptions/TemplateMessageModal';

const ChatList: React.FC = () => {
	const t = useTranslations('Support');
	const { chats, selectedChat, isLoading, hasMore, selectChat, loadMoreChats } = useChatStore();
	const [isTemplateModalOpen, setIsTemplateModalOpen] = React.useState(false);
	const [selectedTag, setSelectedTag] = useState<ChatTag | 'all'>('all');
	const [searchInput, setSearchInput] = useState<string>('');

	useEffect(() => {
		const chatChannel = subscribeToChats();

		return () => {
			if (chatChannel) {
				chatChannel.unsubscribe();
			}
		};
	}, []);

	const formatPhoneNumber = (phone: string) => {
		const cleaned = phone.replace(/\D/g, '');
		if (cleaned.length === 12 && cleaned.startsWith('52')) {
			return `+${cleaned.slice(0, 2)} ${cleaned.slice(2, 5)} ${cleaned.slice(5, 8)} ${cleaned.slice(8)}`;
		}
		if (cleaned.length === 13 && cleaned.startsWith('52')) {
			return `+${cleaned.slice(0, 2)} ${cleaned[2]} ${cleaned.slice(3, 6)} ${cleaned.slice(6, 9)} ${cleaned.slice(9)}`;
		}
		return phone;
	};

	const formatTimestamp = (timestamp: string) => {
		const date = new Date(timestamp);
		const day = date.getDate().toString().padStart(2, '0');
		const month = (date.getMonth() + 1).toString().padStart(2, '0');
		const year = date.getFullYear();
		const hours = date.getHours().toString().padStart(2, '0');
		const minutes = date.getMinutes().toString().padStart(2, '0');

		const isToday = new Date().toDateString() === date.toDateString();

		return isToday
			? `${hours}:${minutes} ${hours >= '12' ? 'PM' : 'AM'}`
			: `${day}/${month}/${year}`;
	};

	// Customer support status configuration
	const getCustomerSupportStatusConfig = (status: Chat['customer_support_status']) => {
		const configs: Record<
			Chat['customer_support_status'],
			{
				color: string;
				textColor: string;
				bgColor: string;
				borderColor: string;
				label: string;
			}
		> = {
			AI_RESPONSES: {
				color: '#0d7ad4',
				textColor: 'text-blue-800',
				bgColor: 'bg-blue-50',
				borderColor: 'border-blue-600',
				label: t('customerSupportStatus.AI_RESPONSES')
			},
			NEEDS_REVIEW: {
				color: '#d4b30d',
				textColor: 'text-yellow-800',
				bgColor: 'bg-yellow-50',
				borderColor: 'border-yellow-500',
				label: t('customerSupportStatus.NEEDS_REVIEW')
			},
			CLIENT_HAS_NOT_ANSWERED: {
				color: '#d4210d',
				textColor: 'text-red-800',
				bgColor: 'bg-red-50',
				borderColor: 'border-red-600',
				label: t('customerSupportStatus.CLIENT_HAS_NOT_ANSWERED')
			},
			ARCHIVED: {
				color: 'black',
				textColor: 'text-gray-800',
				bgColor: 'bg-gray-100',
				borderColor: 'border-gray-800',
				label: t('customerSupportStatus.ARCHIVED')
			},
			ANSWER_SENT: {
				color: '#106e22',
				textColor: 'text-green-800',
				bgColor: 'bg-green-50',
				borderColor: 'border-green-600',
				label: t('customerSupportStatus.ANSWER_SENT')
			},
			PREAPPROVED: {
				color: '#106e22',
				textColor: 'text-green-800',
				bgColor: 'bg-green-50',
				borderColor: 'border-green-600',
				label: t('customerSupportStatus.PREAPPROVED')
			}
		};

		return (
			configs[status] || {
				color: 'black',
				textColor: 'text-gray-800',
				bgColor: 'bg-white',
				borderColor: 'border-gray-300',
				label: ''
			}
		);
	};

	const RenderCustomerSupportStatusIcon: React.FC<{
		status: Chat['customer_support_status'];
		size?: number;
	}> = ({ status, size = 14 }) => {
		const statusConfig = getCustomerSupportStatusConfig(status);
		const iconColor = getAccentColorFromCustomerSupportStatus(status);

		const IconWithTooltip = ({ children }: { children: React.ReactNode }) => (
			<div className="inline-block relative" onMouseDown={(e) => e.stopPropagation()}>
				<div className="group mr-1 ">
					{children}
					<span
						className={`absolute left-1/2 -translate-x-1 -top-8 ${statusConfig.textColor} text-xs ${statusConfig.bgColor} 
						px-2 py-1 rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none
						transition-opacity duration-200 shadow-md text-wrap border ${statusConfig.borderColor}`}
						style={{ zIndex: 100, width: '220px' }}
					>
						{statusConfig.label}
						{/* Triangle pointer */}
						<span
							className={`absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 rotate-45 ${statusConfig.bgColor} border-b ${statusConfig.borderColor} border-r ${statusConfig.borderColor}`}
						></span>
					</span>
				</div>
			</div>
		);

		switch (status) {
			case 'AI_RESPONSES':
				return (
					<IconWithTooltip>
						<Cpu size={size} color={iconColor} />
					</IconWithTooltip>
				);
			case 'NEEDS_REVIEW':
				return (
					<IconWithTooltip>
						<AlertTriangle size={size} color={iconColor} />
					</IconWithTooltip>
				);
			case 'CLIENT_HAS_NOT_ANSWERED':
				return (
					<IconWithTooltip>
						<AlertCircle size={size} color={iconColor} />
					</IconWithTooltip>
				);
			case 'ARCHIVED':
				return (
					<IconWithTooltip>
						<Archive size={size} color={iconColor} />
					</IconWithTooltip>
				);
			case 'ANSWER_SENT':
				return (
					<IconWithTooltip>
						<Send size={size} color={iconColor} />
					</IconWithTooltip>
				);
			case 'PREAPPROVED':
				return (
					<IconWithTooltip>
						<UserCheck size={size} color={iconColor} />
					</IconWithTooltip>
				);
			default:
				return null;
		}
	};

	const getAccentColorFromCustomerSupportStatus = (
		status: Chat['customer_support_status']
	): string => {
		const config = getCustomerSupportStatusConfig(status);
		return config.color;
	};

	const getBorderColorClass = (status: Chat['customer_support_status']): string => {
		const config = getCustomerSupportStatusConfig(status);
		return config.borderColor;
	};

	const getBackgroundColorClass = (status: Chat['customer_support_status']): string => {
		const config = getCustomerSupportStatusConfig(status);
		return config.bgColor;
	};
	return (
		<div className="h-full flex flex-col">
			<div className="shrink-0 border-b bg-gray-50">
				<div className="p-4">
					<div className="flex items-center justify-between">
						<Typography size="lg" weight="semibold">
							<MessageCircle className="inline-block mr-2" size={20} />
							{t('Chats')}
						</Typography>
						<button
							onClick={() => setIsTemplateModalOpen(!isTemplateModalOpen)}
							className="p-2 hover:bg-gray-100 rounded-full transition-colors"
						>
							<Plus size={20} className="text-gray-600" />
						</button>
					</div>
				</div>

				<FiltersAndSearch
					setSelectedTag={setSelectedTag}
					searchInput={searchInput}
					setSearchInput={setSearchInput}
					RenderCustomerSupportStatusIcon={RenderCustomerSupportStatusIcon}
				/>
			</div>

			<div className="flex-1 overflow-y-auto">
				{chats.length === 0 && !isLoading ? (
					<div className="p-4 text-center">
						<Typography color="subtitle">
							{selectedTag === 'all' ? t('noChats') : t('noChatsWithTag')}
						</Typography>
					</div>
				) : (
					<>
						{chats.map((chat: Chat) => {
							const bgColorClass =
								selectedChat?.id === chat.id
									? getBackgroundColorClass(chat.customer_support_status)
									: '';
							const borderColorClass =
								selectedChat?.id === chat.id
									? getBorderColorClass(chat.customer_support_status)
									: '';

							return (
								<div
									key={chat.id}
									className={`p-4 border-b hover:bg-gray-50 transition-colors cursor-pointer
                    ${selectedChat?.id === chat.id ? `${bgColorClass} border-l-4 ${borderColorClass}` : ''}`}
									style={{
										borderLeftColor:
											selectedChat?.id === chat.id
												? getAccentColorFromCustomerSupportStatus(chat.customer_support_status)
												: 'transparent'
									}}
									onClick={() => selectChat(chat)}
								>
									<div className="flex items-center justify-between mb-2">
										<div className="flex flex-col gap-2 flex-grow">
											<div className="flex flex-row justify-between mt-1">
												<div>
													<Typography size="md" weight="medium">
														<div className="flex items-center mt-0.5">
															<RenderCustomerSupportStatusIcon
																status={chat.customer_support_status}
																size={16}
															/>
															{formatPhoneNumber(chat.customer_phone)}
														</div>
													</Typography>
												</div>
												{chat?.last_message_at && (
													<div className="flex items-center mt-0.5">
														<Typography size="sm" color="subtitle">
															{formatTimestamp(chat.last_message_at)}
														</Typography>
													</div>
												)}
											</div>
											{chat?.customer_name && (
												<div className="flex items-center mt-0.5">
													<Typography size="sm" weight="bold">
														{chat.customer_name}
													</Typography>
												</div>
											)}
										</div>
									</div>

									<div className="flex mt-2 space-x-2">
										{chat?.tag && (
											<span className="text-gray-600 text-xs bg-gray-100 px-2 py-1 rounded-full">
												{chat.tag}
											</span>
										)}
									</div>
								</div>
							);
						})}

						{hasMore && (
							<div className="p-4 text-center border-t mb-12">
								<Button
									color="secondary"
									size="auto"
									label={isLoading ? t('loading') : t('LoadMore')}
									onClick={loadMoreChats}
									disabled={isLoading}
								/>
							</div>
						)}
					</>
				)}
			</div>

			<TemplateMessageModal
				isOpen={isTemplateModalOpen}
				onClose={() => setIsTemplateModalOpen(false)}
				isNewChat={true}
			/>
		</div>
	);
};

export default ChatList;
